<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  templateVariables: {
    type: Array,
    default: () => [],
  },
  csvHeaders: {
    type: Array,
    default: () => [],
  },
  variableMappings: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['update:variableMappings']);

const { t } = useI18n();

const headerOptions = computed(() => {
  return props.csvHeaders.map(header => ({
    value: header,
    label: header,
  }));
});

const isVariableMappingComplete = computed(() => {
  if (!props.templateVariables.length) return true;
  return props.templateVariables.every(
    variable => props.variableMappings[variable]
  );
});

const setVariableMapping = (variable, headerValue) => {
  const updatedMappings = { ...props.variableMappings };
  updatedMappings[variable] = headerValue;
  emit('update:variableMappings', updatedMappings);
};

const clearAllMappings = () => {
  const emptyMappings = {};
  props.templateVariables.forEach(variable => {
    emptyMappings[variable] = '';
  });
  emit('update:variableMappings', emptyMappings);
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex justify-between items-start">
      <div>
        <h4 class="text-base font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.VARIABLE_MAPPING.TITLE') }}
        </h4>
        <p class="text-sm text-n-slate-10 mt-1">
          {{
            t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.VARIABLE_MAPPING.DESCRIPTION')
          }}
        </p>
      </div>

      <div class="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          color="slate"
          :label="
            t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.VARIABLE_MAPPING.CLEAR_ALL')
          "
          icon="i-lucide-x"
          @click="clearAllMappings"
        />
      </div>
    </div>

    <div
      v-if="templateVariables.length === 0"
      class="p-4 border rounded-md bg-n-alpha-black2 text-n-slate-10"
    >
      <p>
        {{
          t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.VARIABLE_MAPPING.NO_VARIABLES')
        }}
      </p>
    </div>

    <div v-else class="flex flex-col gap-3">
      <div class="flex justify-end mb-2">
        <span
          class="flex items-center gap-1 text-xs font-medium rounded-full px-3 py-1"
          :class="[
            isVariableMappingComplete
              ? 'bg-green-500 text-white'
              : 'bg-yellow-500 text-black',
          ]"
        >
          <span>
            <i v-if="isVariableMappingComplete" class="i-lucide-check-circle" />
            <i v-else class="i-lucide-alert-circle" />
          </span>
          <span>
            {{
              isVariableMappingComplete
                ? t(
                    'CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.VARIABLE_MAPPING.STATUS_COMPLETE'
                  )
                : t(
                    'CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.VARIABLE_MAPPING.STATUS_INCOMPLETE'
                  )
            }}
          </span>
        </span>
      </div>

      <div
        v-for="variable in templateVariables"
        :key="variable"
        class="flex items-center gap-4 p-3 border rounded-md bg-n-alpha-black2"
      >
        <div class="w-1/4">
          <div class="text-sm font-semibold text-n-slate-12">
            {{ variable }}
          </div>
          <div class="text-xs text-n-slate-10 mt-1">
            {{
              t(
                'CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.VARIABLE_MAPPING.VARIABLE_DESCRIPTION'
              )
            }}
          </div>
        </div>

        <div class="flex-1">
          <ComboBox
            :id="`variable-${variable}`"
            :value="variableMappings[variable]"
            :options="headerOptions"
            :placeholder="
              t(
                'CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.VARIABLE_MAPPING.SELECT_HEADER'
              )
            "
            class="w-full"
            @update:model-value="
              headerValue => setVariableMapping(variable, headerValue)
            "
          />
        </div>

        <div
          v-if="variableMappings[variable]"
          class="flex items-center gap-2 w-1/4"
        >
          <div class="text-n-slate-10">
            {{ t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.VARIABLE_MAPPING.ARROW') }}
          </div>
          <div
            class="px-2 py-1 bg-n-alpha-black2 rounded text-xs font-medium text-n-slate-12"
          >
            {{ variableMappings[variable] }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
